import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn';
import { Button } from '../ui';

export default function ImageUpload({
  onUpload,
  value,
  onRemove,
  disabled = false,
  className,
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await processFile(file);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      await processFile(file);
    }
  };

  const processFile = async (file) => {
    setIsUploading(true);
    try {
      // Create a preview URL
      const previewUrl = URL.createObjectURL(file);
      await onUpload({ file, previewUrl });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  if (value) {
    return (
      <div className={cn('relative inline-block', className)}>
        <img
          src={value}
          alt={t('stimulus.uploadedHandwritten')}
          className="max-w-full max-h-64 rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.1)]"
        />
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className={cn(
              'absolute -top-2 -right-2',
              'w-6 h-6 rounded-full',
              'bg-gray-900 text-white dark:bg-gray-200 dark:text-gray-900',
              'flex items-center justify-center',
              'hover:bg-gray-700 dark:hover:bg-gray-300 transition-colors duration-150',
              'shadow-[0_2px_4px_rgba(0,0,0,0.2)]'
            )}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={className}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        disabled={disabled || isUploading}
        className="sr-only"
      />
      <div
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={cn(
          'flex items-center gap-3 px-4 py-3 rounded-lg',
          'border-2 border-dashed transition-all duration-200 cursor-pointer',
          isDragging
            ? 'border-gray-400 bg-gray-100 dark:border-gray-500 dark:bg-gray-700'
            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-700',
          (disabled || isUploading) && 'opacity-60 cursor-not-allowed'
        )}
      >
        <span className="text-lg" aria-hidden="true">&#128206;</span>
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {isUploading
            ? t('common.uploading')
            : t('stimulus.handwrittenPrompt')}
        </span>
      </div>
    </div>
  );
}
